/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { getContrastingTextColor } from '#utils/PRIVATE_colorUtils';
import { PieLabelInfo } from './labelUtils';
import { styles } from './themes/PieChartStyles.css';

type LabelProps<K> = {
  pieLabelInfo: PieLabelInfo<K>;
  labelPos: 'inside' | 'left' | 'right';
  fontSize: string;
  isRtl: boolean;
};

export function PieChartLabel<K>({ pieLabelInfo, labelPos, fontSize, isRtl }: LabelProps<K>) {
  const { x, y, textInfo, slice, startPt, midPt, endPt } = pieLabelInfo;
  const color = slice.color as string;
  const fill = labelPos === 'inside' ? getContrastingTextColor(color) : undefined;
  return (
    <>
      {textInfo?.lines.map((line: any, i: number) => {
        const lineX = x!;
        const lineY = y! + (i + 0.5) * textInfo.dims[i].height;
        const textAnchor =
          (isRtl && labelPos === 'left') || (!isRtl && labelPos === 'right')
            ? 'start'
            : labelPos === 'inside'
            ? 'middle'
            : 'end';
        return (
          <>
            {slice.label && (
              <text
                className={styles.labelStyle}
                x={lineX}
                y={lineY}
                textAnchor={textAnchor}
                dominantBaseline={labelPos === 'inside' ? 'center' : 'middle'}
                style={{
                  fill,
                  fontSize
                }}>
                {line}
              </text>
            )}
            {slice.hasFeelers && startPt && midPt && endPt && (
              <>
                <line
                  x1={startPt.x}
                  x2={midPt.x}
                  y1={startPt.y}
                  y2={midPt.y}
                  className={styles.fillerLineStyle}></line>
                <line
                  x1={endPt.x}
                  x2={midPt.x}
                  y1={endPt.y}
                  y2={midPt.y}
                  className={styles.fillerLineStyle}></line>
              </>
            )}
          </>
        );
      })}
    </>
  );
}
